import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import {
  setQueryStringParam,
  getQueryStringParams,
  objectToQueryString,
  snapshotSquadsFromCache,
} from "../../shared/utils";

/**
 * DXI Team Breakdown Component
 *
 * Fully self-contained component responsible for rendering the DXI (Developer Experience Index)
 * team breakdown table. This component:
 * - Fetches its own data from the DXI team breakdown endpoint
 * - Responds to benchmark segment changes via window events
 * - Watches for comparison type changes via the global store
 * - Manages its own state for benchmark values and breakdown data
 *
 * This ensures the breakdown updates immediately when benchmark segments or filters change,
 * without relying on parent component state.
 */
export function snapshotDxiBreakdown(config) {
  const { dxiDrilldownTabs, minResponseThreshold, snapshotId } = config;

  return {
    ...teamTreeExpansionMixin(),

    // Configure row ID mapping for DXI data
    rowIdKey: "snapshot_squad_id",
    cachedParentKey: "parent_id",

    // Component state
    drilldownData: {},
    tableDataLoaded: false,
    isFetchingTableData: false,
    dxi50: null,
    dxi75: null,
    dxi90: null,
    orgDxiScore: null,
    minResponseThreshold,

    init() {
      // Initial fetch
      this.fetchTeamBreakdown();

      // Watch for comparison changes
      this.$watch("$store.snapshotResultFilters.compareTo", () => {
        this.fetchTeamBreakdown();
      });

      // Watch for previous snapshot changes
      this.$watch("$store.snapshotResultFilters.previousSnapshotId", () => {
        this.fetchTeamBreakdown();
      });

      // Watch for squad filter changes
      this.$watch("$store.snapshotResultFilters.squadIds", () => {
        this.fetchTeamBreakdown();
      });

      // Watch for tag filter changes
      this.$watch("$store.snapshotResultFilters.tagIds", () => {
        this.fetchTeamBreakdown();
      });
    },

    /**
     * Get cached squads for team hierarchy
     */
    get cachedSquads() {
      return snapshotSquadsFromCache(snapshotId);
    },

    getCachedSquads() {
      return this.cachedSquads || [];
    },

    get reFetchTeamBreakdown() {
      return this.fetchTeamBreakdown();
    },

    /**
     * Build query parameters from global filters
     */
    queryParams() {
      const squadIds = this.$store.snapshotResultFilters.squadIds;
      const tagIds = this.$store.snapshotResultFilters.tagIds;
      const compareTo = this.$store.snapshotResultFilters.compareTo;
      const previousSnapshotId =
        this.$store.snapshotResultFilters.previousSnapshotId;
      const aggregation = this.$store.snapshotResultFilters.aggregation;
      const hierarchyGroup = this.$store.snapshotResultFilters.hierarchyGroup;
      const branch = this.$store.snapshotResultFilters.branch;

      const params = {};

      if (squadIds?.length) params.squad_ids = squadIds.join(",");
      if (tagIds?.length) params.tag_ids = tagIds.join(",");
      if (compareTo) params.ct = compareTo;
      if (previousSnapshotId) params.prev_ss = previousSnapshotId;
      if (aggregation) params.aggregation = aggregation;
      if (hierarchyGroup?.encoded_id) params.hg = hierarchyGroup.encoded_id;
      if (branch) params.branch = branch;

      // Get benchmark segment ID from localStorage
      const { currentUser } = window;
      const benchmarkSegmentCacheKey = `benchmark_segment_id_${currentUser.account_id}`;
      const benchmarkSegmentId = JSON.parse(
        localStorage.getItem(benchmarkSegmentCacheKey),
      );
      if (benchmarkSegmentId) params.bs = benchmarkSegmentId;

      return objectToQueryString(params)
        ? `?${objectToQueryString(params)}`
        : "";
    },

    /**
     * Get the team breakdown endpoint URL
     */
    getTeamBreakdownUrl() {
      const teamBreakdownTab = dxiDrilldownTabs?.find(
        (t) => t.tab === "team_breakdown",
      );
      return teamBreakdownTab?.url || "";
    },

    /**
     * Fetch team breakdown data
     */
    fetchTeamBreakdown() {
      this.resetTreeState();
      this.isFetchingTableData = true;

      const url = this.getTeamBreakdownUrl();
      if (!url) return;

      const fullUrl = url + this.queryParams();

      return fetch(fullUrl)
        .then((resp) => resp.json())
        .then((data) => {
          this.drilldownData.team_breakdown = { data };

          // Update benchmark values from response
          this.dxi50 = data.dxi_50;
          this.dxi75 = data.dxi_75;
          this.dxi90 = data.dxi_90;
          this.orgDxiScore = data.org_dxi_score;

          this.tableDataLoaded = true;
          this.isFetchingTableData = false;
        })
        .catch((error) => {
          console.error("Error fetching DXI team breakdown:", error);
          this.isFetchingTableData = false;
        });
    },

    /**
     * Get processed breakdown items
     */
    get breakdownItems() {
      return this.getBreakdownItems({
        dataPath: "team_breakdown.data.result_items",
        sortBy: this.drilldownSortBy,
        compareTo: this.compareTo,
        reverse: this.drilldownSortReverse,
        dxi50: this.dxi50,
        dxi75: this.dxi75,
        dxi90: this.dxi90,
        orgDxiScore: this.orgDxiScore,
      });
    },

    /**
     * Access global filter states
     */
    get compareTo() {
      return this.$store.snapshotResultFilters.compareTo;
    },

    get drilldownSortBy() {
      return this.$store.snapshotResultFilters.drilldownSortBy;
    },

    get drilldownSortReverse() {
      return this.$store.snapshotResultFilters.drilldownSortReverse;
    },

    get squadIds() {
      return this.$store.snapshotResultFilters.squadIds;
    },

    get tagIds() {
      return this.$store.snapshotResultFilters.tagIds;
    },

    get filtersApplied() {
      return this.squadIds?.length > 0 || this.tagIds?.length > 0;
    },

    /**
     * Calculate DXI comparison values based on current benchmark segment
     * Mostly ripped from the previous version, but a little more paranoid about nulls ;)
     */
    calculateDxiComparison(item) {
      if (this.compareTo == "vs_50th") {
        // Benchmark score excludes custom drivers
        return item.score != null && this.dxi50 != null
          ? item.score - this.dxi50
          : null;
      } else if (this.compareTo == "vs_75th") {
        // Benchmark score excludes custom drivers
        return item.score != null && this.dxi75 != null
          ? item.score - this.dxi75
          : null;
      } else if (this.compareTo == "vs_90th") {
        // Benchmark score excludes custom drivers
        return item.score != null && this.dxi90 != null
          ? item.score - this.dxi90
          : null;
      } else if (this.compareTo == "vs_prev") {
        // Handle both immediate previous and specific older snapshot comparisons
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (this.compareTo == "vs_org") {
        return item.score != null && this.orgDxiScore != null
          ? item.score - this.orgDxiScore
          : null;
      }
      return null;
    },

    /**
     * Handle sorting changes
     */
    changeDrilldownSortBy(sortBy) {
      if (this.drilldownSortBy == sortBy) {
        this.$store.snapshotResultFilters.drilldownSortReverse =
          !this.drilldownSortReverse;

        setQueryStringParam("ddsr", this.drilldownSortReverse);
        return;
      }

      this.$store.snapshotResultFilters.drilldownSortBy = sortBy;
      this.$store.snapshotResultFilters.drilldownSortReverse = false;

      setQueryStringParam("ddsb", sortBy);
      setQueryStringParam("ddsr", false);
    },

    /**
     * Build sort function for breakdown items
     */
    buildSortFunction(config) {
      const { sortBy, compareTo, reverse, dxi50, dxi75, dxi90, orgDxiScore } =
        config || {};

      return (a, b) => {
        let result = 0;

        switch (sortBy) {
          case "snapshotSquadName":
            result = (a.snapshot_squad_name || "").localeCompare(
              b.snapshot_squad_name || "",
            );
            break;
          case "score":
            result = (a.score || 0) - (b.score || 0);
            break;
          case "dxiComparison": {
            const aComparison = this.calculateDxiComparisonForItem(
              a,
              compareTo,
              dxi50,
              dxi75,
              dxi90,
              orgDxiScore,
            );
            const bComparison = this.calculateDxiComparisonForItem(
              b,
              compareTo,
              dxi50,
              dxi75,
              dxi90,
              orgDxiScore,
            );
            if (aComparison === null && bComparison === null) result = 0;
            else if (aComparison === null) result = 1;
            else if (bComparison === null) result = -1;
            else result = aComparison - bComparison;
            break;
          }
          default:
            result = (a.snapshot_squad_name || "").localeCompare(
              b.snapshot_squad_name || "",
            );
        }

        return reverse ? -result : result;
      };
    },

    /**
     * Helper method for calculating comparison in sorting
     */
    calculateDxiComparisonForItem(
      item,
      compareTo,
      dxi50,
      dxi75,
      dxi90,
      orgDxiScore,
    ) {
      if (compareTo == "vs_50th") {
        return item.score != null && dxi50 != null ? item.score - dxi50 : null;
      } else if (compareTo == "vs_75th") {
        return item.score != null && dxi75 != null ? item.score - dxi75 : null;
      } else if (compareTo == "vs_90th") {
        return item.score != null && dxi90 != null ? item.score - dxi90 : null;
      } else if (compareTo == "vs_prev") {
        // Handle both immediate previous and specific older snapshot comparisons
        return item.previous_score != null
          ? item.score - item.previous_score
          : null;
      } else if (compareTo == "vs_org") {
        return item.score != null && orgDxiScore != null
          ? item.score - orgDxiScore
          : null;
      }
      return null;
    },

    // Required methods for teamTreeExpansionMixin
    getChildDataEndpoint() {
      return this.getTeamBreakdownUrl();
    },

    getChildDataParams() {
      const currentParams = Object.fromEntries(
        getQueryStringParams().entries(),
      );

      return {
        ...currentParams,
        tag_ids: this.tagIds.join(","),
        // These will be added by the mixin:
        // aggregation: "selected_teams",
        // squad_ids: "child,squad,ids"
      };
    },

    /**
     * Handle aggregation changes
     */
    handleAggregationChange() {
      setTimeout(() => {
        this.fetchTeamBreakdown();
      }, 0);
    },
  };
}
